import {Component, OnDestroy, OnInit} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {PasskeyService} from '../../_services';
import {isPasskeySupported, isSilentCeremonyFailure, newAbortController, whenPageReady} from '../models/passkey.util';
import {completePostLogin} from '../models/post-login';
import {UserIdentity} from '../../helpers/common/user.identity';
import {environment} from '../../../environments/environment';

@Component({
  moduleId: module.id,
  selector: 'app-passkey-verify',
  templateUrl: `../views/passkey-verify.component.html`,
  styleUrls: ['../styles/auth-style.scss'],
  providers: [MessageService]
})
export class PasskeyVerifyComponent implements OnInit, OnDestroy {
  public logoUrl = environment.logoUrl;
  public loginToken: string = null;
  public hasRecoveryCodes = false;
  public hasLostDeviceLink = true;
  public verifying = false;
  public resetEmailSent = false;
  public supported = isPasskeySupported();
  // Automatic prompt: at most one per visit, and none once the user clicked.
  private ceremonyStarted = false;
  private destroyed = false;
  private cancelAutoStart: () => void = () => {};
  // The open ceremony: aborted when the screen is left, which closes the OS sheet.
  private ceremony: AbortController | null = null;
  // Browser Back / Forward onto this screen: Angular restores history.state,
  // i.e. the old login_token - most likely already spent by the sign-in.
  private reenteredByHistory = false;

  constructor(
    private router: Router,
    private passkeyService: PasskeyService,
    private messageService: MessageService,
    private translate: TranslateService
  ) {
    const navigation = this.router.getCurrentNavigation();
    this.reenteredByHistory = !!navigation && (navigation as any).trigger === 'popstate';
    const state = navigation && navigation.extras && navigation.extras.state;
    if (state) {
      this.loginToken = (state as any).loginToken;
      this.hasRecoveryCodes = !!(state as any).hasRecoveryCodes;
    }
  }

  ngOnInit() {
    // Already signed in (e.g. Back after the sign-in): this step is done.
    // Same as LoginGuard does for /login - into the app, no dead screen.
    if (UserIdentity.isAuthenticated()) {
      // replaceUrl: Back must not land here again and bounce forward.
      this.router.navigate([''], {replaceUrl: true});
      return;
    }
    // Reached directly, by refresh or a pasted URL, there is no token and no
    // way to get one here. Send them back rather than showing a dead screen.
    if (!this.loginToken) {
      this.router.navigate(['/login']);
      return;
    }
    // Open the passkey prompt without a click - the browser / OS sheet already
    // lists every passkey provider on the device. Once, and only while the
    // page is visible (a background tab must not raise an OS dialog). The
    // button stays as the fallback. Not when Back / Forward re-entered the
    // screen: its restored login_token is likely spent, and an automatic
    // attempt would 403 into a toast and a bounce nobody asked for.
    if (this.supported && !this.reenteredByHistory) {
      this.cancelAutoStart = whenPageReady(() => this.autoVerify());
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.cancelAutoStart();
    if (this.ceremony) {
      this.ceremony.abort();
    }
  }

  private autoVerify() {
    if (this.destroyed || this.ceremonyStarted || this.verifying || !this.supported || !this.loginToken) {
      return;
    }
    this.verify(true);
  }

  /** `auto`: the automatic prompt (no click) - a cancel there is not toasted. */
  async verify(auto = false) {
    if (!this.supported || this.verifying) {
      return;
    }
    this.ceremonyStarted = true;
    this.verifying = true;

    // The three steps are caught separately because only two of them spend
    // the login_token.
    //
    // PasskeyService::claimChallenge() (jobrocksapi) consumes the challenge
    // at the START of login-verify, before the assertion is checked, to close
    // a replay window. So once a request has gone out and come back an error,
    // this login_token is dead: "Use passkey", "Use a recovery code instead"
    // and the recovery screen's "Try passkey again" would all 403 from here
    // on, and the recovery screen would call a perfectly good code invalid.
    // There is nothing left to do on this screen, so those paths bounce back
    // to /login for a fresh token.
    let optionsResponse: any;
    try {
      optionsResponse = await this.passkeyService.loginOptions(this.loginToken).toPromise();
    } catch (error) {
      if (this.destroyed) {
        this.verifying = false;   // the screen is gone: no toast, no bounce
        return;
      }
      this.failAndReturnToLogin();
      return;
    }

    // navigator.credentials.get() is purely local: a cancelled prompt
    // (NotAllowedError), a timeout (also NotAllowedError), an aborted one
    // (AbortError) or any other authenticator-side failure sends no request
    // at all, so the challenge is still unclaimed and the token still good.
    // Stay put and let them try again - this is the everyday case of somebody
    // dismissing the system passkey sheet, and it must not log them out.
    // Cancelling the automatic prompt (no passkey here, Safari refusing a
    // prompt nobody clicked for) is not even worth a toast: the screen with
    // its button and the other options is the answer.
    // Leaving the screen ("Back to login", a recovery link) aborts it: that
    // AbortError is ours, not the user's failure - no toast, the token is
    // untouched. An assertion that arrives after the screen is gone is not sent.
    if (this.destroyed) {
      this.verifying = false;
      return;
    }
    let assertion: any;
    this.ceremony = newAbortController();
    try {
      assertion = await this.passkeyService.getCredential(optionsResponse.options, this.ceremony ? this.ceremony.signal : undefined);
    } catch (error) {
      this.verifying = false;
      if (this.destroyed || (auto && isSilentCeremonyFailure(error))) {
        return;
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('Error!'),
        detail: this.translate.instant('passkey verification failed')
      });
      return;
    } finally {
      this.ceremony = null;
    }

    if (this.destroyed) {
      this.verifying = false;
      return;
    }

    let response: any;
    try {
      response = await this.passkeyService.loginVerify(this.loginToken, assertion).toPromise();
    } catch (error) {
      if (this.destroyed) {
        this.verifying = false;   // the screen is gone: no toast, no bounce
        return;
      }
      this.failAndReturnToLogin();
      return;
    }

    // Deliberately outside the try: the ceremony itself already succeeded
    // (the server verified the assertion), so anything that goes wrong from
    // here on is a routing/payload-shape problem, not a failed verification
    // - it must not be swallowed into the same "verification failed" toast,
    // which would be both wrong and would leave a half-written session with
    // no explanation.
    this.finishLogin(response);
  }

  /**
   * A request went out and came back an error, so the login_token is spent.
   * Toast with the given message, then send them back to the password step -
   * same shape whether the trigger was a failed login-verify or
   * recoveryEmail() hitting a dead login_token (JS-1396 whole-branch review
   * finding 2), including the delay that lets the toast be read before the
   * screen changes.
   */
  private returnToLoginWithExpiredToken(messageTranslationKey: string) {
    this.verifying = false;
    // The recovery link would hand the spent token to the recovery screen,
    // which would then report a perfectly good code as invalid, and "I lost
    // my device and my recovery codes" would hand it to recoveryEmail(),
    // which now answers 403 for it instead of a deceptive 200 ("set up a
    // passkey on this device", shown on the same flag, would 403 the same
    // way). Hiding them closes the window between the toast and the
    // navigation.
    this.hasRecoveryCodes = false;
    this.hasLostDeviceLink = false;
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('Error!'),
      detail: this.translate.instant(messageTranslationKey)
    });
    setTimeout(() => this.router.navigate(['/login']), 2500);
  }

  /**
   * A request went out and came back an error, so the login_token is spent.
   * Same shape as the recovery screen's exhausted-budget path.
   */
  private failAndReturnToLogin() {
    this.returnToLoginWithExpiredToken('passkey verification failed');
  }

  /**
   * No passkey on this device yet (a phone, a second laptop, an Incognito
   * window that cannot see a browser-extension passkey): add one via an
   * emailed code instead of spending a recovery code. Hidden together with
   * the lost-device link once the login_token is known to be spent.
   */
  setUpOnThisDevice() {
    this.router.navigate(['/passkey-device-code'], {state: {loginToken: this.loginToken}});
  }

  useRecoveryCode() {
    this.router.navigate(['/passkey-recovery'], {state: {loginToken: this.loginToken}});
  }

  /**
   * [A] Lost the device AND the codes. Only reachable from this screen,
   * which means the password was already correct — that is what keeps the
   * emailed reset a second factor rather than a replacement for one.
   *
   * The api answers 200 for a live token (whether or not it maps to a user
   * row) and 403 for a dead one (JS-1396 whole-branch review finding 2). The
   * 403 case reachable right after a failed login-verify is what
   * hasLostDeviceLink hides during its 2.5s window, so that one never gets
   * here - but this same token can also simply outlive its TTL while the
   * user is still on this screen hunting for their device, so a 403 here is
   * real and must not be folded into the same confirmation as success: that
   * would still be the lie the finding was about, just from a caller that
   * ignored an honest status. recoveryEmail() goes through rawHttp (see
   * passkey.service.ts), so `error` here is a genuine HttpErrorResponse with
   * a trustworthy `.status`, not a re-thrown app error.
   */
  requestResetEmail() {
    this.passkeyService.recoveryEmail(this.loginToken).subscribe(
      () => this.resetEmailSent = true,
      (error: HttpErrorResponse) => {
        if (error && error.status === 403) {
          this.returnToLoginWithExpiredToken('This sign-in attempt has expired. Please log in again.');
          return;
        }
        // Any other failure (network drop, 5xx, ...): whether the request
        // ever reached the server is genuinely unknown, so keep the same
        // neutral confirmation as success rather than trade the original lie
        // ("sent" when it was not) for a new one the other way ("expired"
        // when it may just have been a flaky connection).
        this.resetEmailSent = true;
      }
    );
  }

  backToLogin() {
    this.router.navigate(['/login']);
  }

  // The whole post-login sequence (temp-password redirect, currentUser
  // write, language, permission ladder) is shared with login.component.ts and
  // passkey-recovery.component.ts - see post-login.ts. login-verify returns
  // exactly what /login returns on success, minus the `token` on a
  // temp-password response, which is why this path routes to /login rather
  // than /password-reset/:token.
  private finishLogin(response: any) {
    completePostLogin(response, {
      router: this.router,
      translate: this.translate,
      tempPasswordDestination: 'login'
    });
  }
}
